// -----------------------------------------------------------------------------
// ContactDashboard.jsx — Full-Screen Layout
// Props:
//   embedded={bool}  — height:100% mode for Drawer/Card parents. Default false.
//   onClose={fn}     — renders an X close button and enables AppShell-aware
//                      bottom padding so the nav bar doesn't overlap content.
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Card, CardContent, Avatar, IconButton, Typography, Grid, TextField,
  Button, Divider, CircularProgress, useMediaQuery, Stack, Tooltip, Dialog,
  DialogContent, Select, MenuItem, InputLabel, FormControl, Chip, Tab, Tabs,
  Paper, LinearProgress, InputAdornment, Fade, Alert, Snackbar, Slide,
} from "@mui/material";
import { useTheme, alpha, styled } from "@mui/material/styles";
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Call as CallIcon,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  CalendarToday as CalendarIcon,
  LocalOffer as LocalOfferIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Notes as NotesIcon,
  AccessTime as TimeIcon,
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  SentimentVeryDissatisfied,
} from "@mui/icons-material";

import { useQuery, useMutation, useQueryClient } from "react-query";
import API from "../../API";
import AlertDialog from "../../Components/AlertDialog";
import useAlertDialog from "../../Components/useAlertDialog";

// Must match BAR_HEIGHT in AppShell.jsx
const APPSHELL_BAR_HEIGHT = 86;
const APPSHELL_GUTTER     = 12;
const APPSHELL_TOTAL      = APPSHELL_BAR_HEIGHT + APPSHELL_GUTTER; // 98px

/* ─────────────────── Helpers ─────────────────── */
const iso = (d) =>
  new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/* ─────────────────── Styled slide-up transition for dialog ─────────────────── */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ─────────────────── Tags Chip Input ─────────────────── */
function TagsChipInput({ value = [], onChange, labelText = "Tags" }) {
  const [draft, setDraft] = useState("");
  const theme = useTheme();
  const addTag = (tagStr) => {
    const t = (tagStr ?? draft).trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };
  return (
    <Box>
      <TextField
        label={labelText}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
        }}
        fullWidth size="small" placeholder="Type and press Enter"
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
      />
      <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {value.map((tag) => (
          <Chip
            key={tag} label={tag}
            onDelete={() => onChange(value.filter((t) => t !== tag))}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 1.5,
              fontWeight: 600,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ─────────────────── Interactive Star Rating ─────────────────── */
const RATING_LABELS = ["", "Poor", "Fair", "Good", "Great", "Excellent"];
const RATING_ICONS  = [null,
  <SentimentVeryDissatisfied />,
  <SentimentDissatisfied />,
  <SentimentNeutral />,
  <SentimentSatisfied />,
  <SentimentVerySatisfied />,
];
const RATING_COLORS = ["", "error.main", "warning.main", "info.main", "success.main", "success.dark"];

function StarRatingInput({ value = 0, onChange }) {
  const [hovered, setHovered] = useState(0);
  const theme = useTheme();
  const active = hovered || value;

  return (
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
        {[1, 2, 3, 4, 5].map((s) => {
          const filled = s <= active;
          return (
            <Box
              key={s}
              onMouseEnter={() => setHovered(s)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => onChange(s)}
              sx={{
                cursor: "pointer",
                color: filled ? "warning.main" : alpha(theme.palette.text.primary, 0.18),
                transition: "transform 0.15s, color 0.15s",
                display: "flex",
                "&:hover": { transform: "scale(1.3)" },
              }}
            >
              {filled
                ? <StarIcon sx={{ fontSize: 34 }} />
                : <StarBorderIcon sx={{ fontSize: 34 }} />}
            </Box>
          );
        })}
      </Stack>

      {/* Sentiment chip */}
      {active > 0 && (
        <Fade in key={active}>
          <Chip
            icon={React.cloneElement(RATING_ICONS[active], { sx: { fontSize: "18px !important" } })}
            label={RATING_LABELS[active]}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: alpha(theme.palette[RATING_COLORS[active].split(".")[0]][RATING_COLORS[active].split(".")[1] || "main"], 0.12),
              color: RATING_COLORS[active],
              border: `1px solid ${alpha(theme.palette[RATING_COLORS[active].split(".")[0]][RATING_COLORS[active].split(".")[1] || "main"], 0.3)}`,
            }}
          />
        </Fade>
      )}
    </Box>
  );
}

/* ─────────────────── Stats Card ─────────────────── */
function StatsCard({ icon, title, value, color = "primary" }) {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{
      background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.12)} 0%, ${alpha(theme.palette[color].main, 0.04)} 100%)`,
      border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
      borderRadius: 3,
      transition: "all 0.25s",
      "&:hover": { transform: "translateY(-2px)", boxShadow: `0 6px 16px ${alpha(theme.palette[color].main, 0.18)}` },
    }}>
      <CardContent sx={{ py: 2, px: 2.5, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.text.primary, 0.55), fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: theme.palette[color].main, lineHeight: 1 }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette[color].main, 0.15), color: theme.palette[color].main }}>
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ─────────────────── Compact Contact Row ─────────────────── */
function ContactRow({ contact, selected, onClick }) {
  const theme = useTheme();
  const isSelected = selected?.id === contact.id;
  return (
    <Box
      onClick={() => onClick(contact)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5,
        cursor: "pointer", transition: "all 0.15s",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : "transparent",
        borderLeft: `3px solid ${isSelected ? theme.palette.primary.main : "transparent"}`,
        "&:hover": { bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.hover, 0.5) },
      }}
    >
      <Avatar src={contact.profile_photo_link}
        sx={{ width: 42, height: 42, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontSize: "0.9rem", fontWeight: 700, flexShrink: 0 }}
      >
        {contact.first_name?.[0]}{contact.last_name?.[0]}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={isSelected ? 700 : 600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {contact.first_name} {contact.last_name}
        </Typography>
        {(contact.company || contact.title) && (
          <Typography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {contact.title}{contact.title && contact.company ? " · " : ""}{contact.company}
          </Typography>
        )}
        {contact.tags?.length > 0 && (
          <Box sx={{ mt: 0.5, display: "flex", gap: 0.4, flexWrap: "nowrap", overflow: "hidden" }}>
            {contact.tags.slice(0, 2).map((tag) => (
              <Chip key={tag} label={tag} size="small"
                sx={{ height: 18, fontSize: "0.65rem", bgcolor: alpha(theme.palette.info.main, 0.1), color: "info.main", borderRadius: 1 }} />
            ))}
            {contact.tags.length > 2 && (
              <Typography variant="caption" color="text.secondary">+{contact.tags.length - 2}</Typography>
            )}
          </Box>
        )}
      </Box>
      {contact.ranking && (
        <Chip label={`#${contact.ranking}`} size="small"
          sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.15), color: "warning.dark", flexShrink: 0, borderRadius: 1 }} />
      )}
    </Box>
  );
}

/* ─────────────────── Contact Detail Panel ─────────────────── */
function ContactDetailPanel({ contact, editing, onChange, onSave, onDelete, onQuickCall, saving, isMobile }) {
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState("view");
  const [localFavorite, setLocalFavorite] = useState(false);

  useEffect(() => { setActiveSection("view"); }, [contact?.id]);

  if (!contact) return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2, color: "text.secondary", p: 4 }}>
      <Box sx={{ width: 96, height: 96, borderRadius: "50%", bgcolor: alpha(theme.palette.primary.main, 0.06), display: "flex", alignItems: "center", justifyContent: "center" }}>
        <GroupsIcon sx={{ fontSize: 56, opacity: 0.25 }} />
      </Box>
      <Typography variant="h6" fontWeight={600} sx={{ opacity: 0.35 }}>Select a contact</Typography>
      <Typography variant="body2" sx={{ opacity: 0.25 }}>Choose someone from the list to view details</Typography>
    </Box>
  );

  const val = (field) => editing?.[field] ?? contact?.[field] ?? "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: "background.paper", flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Avatar src={contact.profile_photo_link}
            sx={{ width: 64, height: 64, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontSize: "1.4rem", fontWeight: 700, border: `3px solid ${alpha(theme.palette.primary.main, 0.15)}`, flexShrink: 0 }}
          >
            {contact.first_name?.[0]}{contact.last_name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {contact.first_name} {contact.last_name}
                </Typography>
                {contact.title && <Typography variant="body2" color="text.secondary" fontWeight={500}>{contact.title}</Typography>}
                {contact.company && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{contact.company}</Typography>
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                <Tooltip title={localFavorite ? "Unfavorite" : "Favorite"}>
                  <IconButton size="small" onClick={() => setLocalFavorite(!localFavorite)}>
                    {localFavorite ? <StarIcon sx={{ color: "warning.main", fontSize: 20 }} /> : <StarBorderIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Tooltip>
                <Button variant="outlined" size="small" startIcon={<CallIcon />} onClick={() => onQuickCall(contact)}
                  sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.8rem" }}>
                  Log Call
                </Button>
              </Stack>
            </Stack>
            {contact.ranking && (
              <Chip label={`Ranked #${contact.ranking}`} size="small"
                sx={{ mt: 1, fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.12), color: "warning.dark", borderRadius: 1.5 }} />
            )}
          </Box>
        </Stack>
        <Tabs value={activeSection} onChange={(_, v) => setActiveSection(v)} sx={{
          mt: 1.5, minHeight: 36,
          "& .MuiTab-root": { textTransform: "none", minHeight: 36, py: 0.5, fontSize: "0.85rem", fontWeight: 600 },
        }}>
          <Tab value="view" label="Details" />
          <Tab value="edit" label="Edit" icon={<EditIcon sx={{ fontSize: 15 }} />} iconPosition="end" />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {activeSection === "view" ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>Contact Info</Typography>
            <Stack spacing={2} sx={{ mt: 1.5, mb: 3 }}>
              {contact.email && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <EmailIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2">{contact.email}</Typography>
                </Stack>
              )}
              {contact.phone && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PhoneIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2">{contact.phone}</Typography>
                </Stack>
              )}
              {contact.location && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <LocationOnIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2">{contact.location}</Typography>
                </Stack>
              )}
            </Stack>

            {contact.tags?.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>Tags</Typography>
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {contact.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" icon={<LocalOfferIcon sx={{ fontSize: 13 }} />}
                      sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: "info.main", borderRadius: 1.5 }} />
                  ))}
                </Box>
              </>
            )}
            {contact.notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>Notes</Typography>
                <Paper elevation={0} sx={{ mt: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                    {contact.notes}
                  </Typography>
                </Paper>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}><TextField label="Profile Photo URL" fullWidth size="small" value={val("profile_photo_link")} onChange={(e) => onChange("profile_photo_link", e.target.value)} /></Grid>
              <Grid item xs={6}><TextField label="First Name" fullWidth size="small" required value={val("first_name")} onChange={(e) => onChange("first_name", e.target.value)} /></Grid>
              <Grid item xs={6}><TextField label="Last Name" fullWidth size="small" required value={val("last_name")} onChange={(e) => onChange("last_name", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Email" type="email" fullWidth size="small" value={val("email")} onChange={(e) => onChange("email", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Phone" fullWidth size="small" value={val("phone")} onChange={(e) => onChange("phone", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Company" fullWidth size="small" value={val("company")} onChange={(e) => onChange("company", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Title" fullWidth size="small" value={val("title")} onChange={(e) => onChange("title", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Location" fullWidth size="small" value={val("location")} onChange={(e) => onChange("location", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Ranking" type="number" fullWidth size="small" value={val("ranking")} onChange={(e) => onChange("ranking", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField label="Notes" fullWidth multiline minRows={3} size="small" value={val("notes")} onChange={(e) => onChange("notes", e.target.value)} /></Grid>
              <Grid item xs={12}><TagsChipInput value={val("tags") || []} onChange={(tags) => onChange("tags", tags)} /></Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {activeSection === "edit" && (
        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: "background.paper", flexShrink: 0, pb: isMobile ? "calc(16px + env(safe-area-inset-bottom, 0px))" : 2, display: "flex", gap: 1.5 }}>
          <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete(contact.id)}
            sx={{ textTransform: "none", borderRadius: 2 }}>Delete</Button>
          <Button variant="contained" fullWidth disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={onSave} sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}>
            Save Changes
          </Button>
        </Box>
      )}
    </Box>
  );
}

/* ─────────────────── Add Contact Form Panel ─────────────────── */
function AddContactPanel({ data, onChange, onSave, onClose, saving, isMobile }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, transparent)` }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AddIcon color="primary" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>New Contact</Typography>
            <Typography variant="caption" color="text.secondary">Fill in the details below</Typography>
          </Box>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", p: 3, WebkitOverflowScrolling: "touch" }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}><TextField label="Profile Photo URL" fullWidth size="small" value={data.profile_photo_link} onChange={(e) => onChange("profile_photo_link", e.target.value)} /></Grid>
          <Grid item xs={6}><TextField label="First Name" fullWidth size="small" required value={data.first_name} onChange={(e) => onChange("first_name", e.target.value)} /></Grid>
          <Grid item xs={6}><TextField label="Last Name" fullWidth size="small" required value={data.last_name} onChange={(e) => onChange("last_name", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Email" type="email" fullWidth size="small" value={data.email} onChange={(e) => onChange("email", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Phone" fullWidth size="small" value={data.phone} onChange={(e) => onChange("phone", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Company" fullWidth size="small" value={data.company} onChange={(e) => onChange("company", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Title" fullWidth size="small" value={data.title} onChange={(e) => onChange("title", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Location" fullWidth size="small" value={data.location} onChange={(e) => onChange("location", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Ranking" type="number" fullWidth size="small" value={data.ranking} onChange={(e) => onChange("ranking", e.target.value)} /></Grid>
          <Grid item xs={12}><TextField label="Notes" fullWidth multiline minRows={3} size="small" value={data.notes} onChange={(e) => onChange("notes", e.target.value)} /></Grid>
          <Grid item xs={12}><TagsChipInput value={data.tags || []} onChange={(tags) => onChange("tags", tags)} /></Grid>
        </Grid>
      </Box>
      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: "background.paper", flexShrink: 0, pb: isMobile ? "calc(16px + env(safe-area-inset-bottom, 0px))" : 2, display: "flex", gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", borderRadius: 2 }}>Cancel</Button>
        <Button variant="contained" fullWidth disabled={saving || !data.first_name}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          onClick={onSave} sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}>
          Add Contact
        </Button>
      </Box>
    </Box>
  );
}

/* ─────────────────── Call Logs Panel ─────────────────── */
function CallLogsPanel({ logs, contacts, onAdd, onEdit, onDelete }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>Recent Interactions</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd}
            sx={{ textTransform: "none", borderRadius: 2 }}>Log Call</Button>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {logs.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2, color: "text.secondary" }}>
            <Box sx={{ width: 88, height: 88, borderRadius: "50%", bgcolor: alpha(theme.palette.primary.main, 0.06), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CallIcon sx={{ fontSize: 48, opacity: 0.25 }} />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ opacity: 0.35 }}>No call logs yet</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd} sx={{ textTransform: "none", borderRadius: 2 }}>
              Log First Call
            </Button>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {logs.map((log) => {
              const c = contacts.find((x) => x.id === log.contact_id) || {};
              return (
                <Paper key={log.id} elevation={0} sx={{
                  p: 2, mb: 1.5, border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, borderRadius: 2.5,
                  transition: "all 0.15s",
                  "&:hover": { boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`, borderColor: alpha(theme.palette.primary.main, 0.25) },
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={c.profile_photo_link}
                        sx={{ width: 38, height: 38, bgcolor: alpha(theme.palette.primary.main, 0.1), color: "primary.main", fontSize: "0.85rem" }}>
                        {c.first_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CalendarIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">{iso(log.datetime_of_call)}</Typography>
                        </Stack>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      {[1,2,3,4,5].map((s) => (
                        <StarIcon key={s} sx={{ fontSize: 13, color: s <= (log.rating || 0) ? "warning.main" : alpha(theme.palette.text.primary, 0.15) }} />
                      ))}
                      <IconButton size="small" onClick={() => onEdit(log)} sx={{ ml: 0.5 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" color="error" onClick={() => onDelete(log.id)}><DeleteForeverIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Stack>
                  </Stack>
                  {log.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{
                      mt: 1, pl: "52px", overflow: "hidden", textOverflow: "ellipsis",
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.5,
                    }}>
                      {log.notes}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ─────────────────── Analytics Panel ─────────────────── */
function AnalyticsPanel({ contacts, logs, summary }) {
  const theme = useTheme();
  const topContacts = useMemo(() => {
    const counts = {};
    logs.forEach((l) => { counts[l.contact_id] = (counts[l.contact_id] || 0) + 1; });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a).slice(0, 5)
      .map(([id, count]) => ({ contact: contacts.find((c) => c.id === Number(id)), count }))
      .filter((x) => x.contact);
  }, [logs, contacts]);
  const maxCount = topContacts[0]?.count || 1;

  return (
    <Box sx={{ p: 3, overflowY: "auto", height: "100%", WebkitOverflowScrolling: "touch" }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Communication Analytics</Typography>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><StatsCard icon={<GroupsIcon />} title="Contacts" value={contacts.length} color="primary" /></Grid>
        <Grid item xs={6} sm={3}><StatsCard icon={<CallIcon />} title="Calls" value={summary?.total_calls || 0} color="success" /></Grid>
        <Grid item xs={6} sm={3}><StatsCard icon={<TrendingUpIcon />} title="Avg Rating" value={summary?.average_rating?.toFixed(1) || "—"} color="warning" /></Grid>
        <Grid item xs={6} sm={3}><StatsCard icon={<LocalOfferIcon />} title="Total Logs" value={logs.length} color="info" /></Grid>
      </Grid>
      {topContacts.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Most Contacted</Typography>
          <Stack spacing={2}>
            {topContacts.map(({ contact, count }) => (
              <Box key={contact.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={contact.profile_photo_link} sx={{ width: 28, height: 28, fontSize: "0.75rem" }}>{contact.first_name?.[0]}</Avatar>
                    <Typography variant="body2" fontWeight={600}>{contact.first_name} {contact.last_name}</Typography>
                  </Stack>
                  <Chip label={`${count} calls`} size="small" sx={{ fontWeight: 700, height: 20, fontSize: "0.65rem", borderRadius: 1 }} />
                </Stack>
                <LinearProgress variant="determinate" value={(count / maxCount) * 100}
                  sx={{ borderRadius: 4, height: 6, bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   REDESIGNED CALL LOG DIALOG
   - Gradient header with icon
   - Slide-up entry animation
   - Avatar contact selector with rich display  
   - Interactive star rating with sentiment feedback
   - Character count on notes
   - Compact action bar (no DialogActions)
───────────────────────────────────────────────────────────────────────────── */
function CallLogDialog({ open, onClose, logForm, setLogForm, contacts, onSave, onDelete, saving }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isEditing = Boolean(logForm.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      TransitionComponent={SlideUp}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: isMobile ? 0 : 4,
          overflow: "hidden",
          boxShadow: `0 40px 80px ${alpha(theme.palette.common.black, 0.28)}`,
        },
      }}
    >
      {/* ── Gradient Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        px: 3, pt: 3, pb: 2.5, position: "relative",
        borderBottom: `1px solid ${alpha("#fff", 0.08)}`,
      }}>
        {/* X button */}
        <IconButton
          size="small" onClick={onClose}
          sx={{
            position: "absolute", top: 14, right: 14,
            color: "white",
            bgcolor: alpha("#fff", 0.12),
            "&:hover": { bgcolor: alpha("#fff", 0.22), transform: "rotate(90deg)", transition: "all 0.2s" },
            transition: "all 0.2s",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 52, height: 52, borderRadius: "50%",
            bgcolor: alpha("#fff", 0.18),
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}`,
          }}>
            <CallIcon sx={{ color: "white", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: "white", lineHeight: 1.2 }}>
              {isEditing ? "Edit Interaction" : "Log Interaction"}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha("#fff", 0.7) }}>
              {isEditing ? "Update this call or meeting" : "Record a call, meeting, or touchpoint"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* ── Body ── */}
      <DialogContent sx={{ p: 0, overflowX: "hidden" }}>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Contact selector */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1, display: "block", mb: 1 }}>
              Contact
            </Typography>
            <FormControl fullWidth>
              <Select
                value={logForm.contact_id}
                onChange={(e) => setLogForm((p) => ({ ...p, contact_id: e.target.value }))}
                displayEmpty
                renderValue={(val) => {
                  if (!val) return <Typography color="text.disabled" variant="body2">Choose a contact…</Typography>;
                  const c = contacts.find((x) => x.id === val);
                  return c ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={c.profile_photo_link} sx={{ width: 26, height: 26, fontSize: "0.72rem" }}>{c.first_name?.[0]}</Avatar>
                      <Typography variant="body2" fontWeight={600}>{c.full_name || `${c.first_name} ${c.last_name}`}</Typography>
                    </Stack>
                  ) : null;
                }}
                sx={{ borderRadius: 2.5 }}
              >
                {contacts.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ py: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={c.profile_photo_link} sx={{ width: 32, height: 32, fontSize: "0.8rem" }}>{c.first_name?.[0]}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{c.full_name || `${c.first_name} ${c.last_name}`}</Typography>
                        {c.company && <Typography variant="caption" color="text.secondary">{c.company}</Typography>}
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Date & time */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1, display: "block", mb: 1 }}>
              Date & Time
            </Typography>
            <Paper elevation={0} sx={{
              px: 2, py: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 2.5,
              bgcolor: alpha(theme.palette.background.default, 0.5),
            }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TimeIcon sx={{ color: "primary.main", flexShrink: 0, fontSize: 20 }} />
                <TextField
                  type="datetime-local"
                  fullWidth size="small"
                  value={logForm.datetime?.slice(0, 16) || ""}
                  onChange={(e) => setLogForm((p) => ({ ...p, datetime: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiInputBase-root": { bgcolor: "transparent", p: 0 },
                  }}
                />
              </Stack>
            </Paper>
          </Box>

          {/* Star rating */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1, display: "block", mb: 1.5 }}>
              Interaction Quality
            </Typography>
            <Paper elevation={0} sx={{
              px: 2.5, py: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 2.5,
              bgcolor: alpha(theme.palette.warning.main, 0.03),
            }}>
              <StarRatingInput
                value={logForm.rating || 0}
                onChange={(r) => setLogForm((p) => ({ ...p, rating: r }))}
              />
            </Paper>
          </Box>

          {/* Notes */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1, display: "block", mb: 1 }}>
              Notes
            </Typography>
            <Paper elevation={0} sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 2.5, overflow: "hidden",
            }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <NotesIcon sx={{ color: "text.secondary", mt: 0.25, flexShrink: 0, fontSize: 20 }} />
                  <TextField
                    multiline minRows={3} maxRows={7} fullWidth
                    placeholder="What was discussed? Key takeaways, next steps, action items…"
                    value={logForm.notes || ""}
                    onChange={(e) => setLogForm((p) => ({ ...p, notes: e.target.value }))}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-root": { bgcolor: "transparent", p: 0, alignItems: "flex-start" },
                    }}
                  />
                </Stack>
              </Box>
              <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                <Typography variant="caption" color="text.disabled">{(logForm.notes || "").length} chars</Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      {/* ── Action Bar ── */}
      <Box sx={{
        px: 3, py: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        bgcolor: alpha(theme.palette.background.default, 0.6),
        display: "flex", gap: 1.5, alignItems: "center",
      }}>
        {isEditing && (
          <Button
            variant="outlined" color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => { onDelete(logForm.id); onClose(); }}
            sx={{ textTransform: "none", borderRadius: 2, flexShrink: 0 }}
          >
            Delete
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: "none", borderRadius: 2, color: "text.secondary" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!logForm.contact_id || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          onClick={onSave}
          sx={{
            textTransform: "none", borderRadius: 2, fontWeight: 700, px: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
            "&:hover": { boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.45)}` },
            "&:disabled": { opacity: 0.5 },
          }}
        >
          {isEditing ? "Save Changes" : "Log Interaction"}
        </Button>
      </Box>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   Props:
     embedded  — use height:100% flex instead of position:fixed
     onClose   — renders X exit button and enables AppShell bottom padding
───────────────────────────────────────────────────────────────────────────── */
export default function ContactDashboard({ embedded = false, onClose = null }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const qc       = useQueryClient();
  const { dialogState } = useAlertDialog();

  // When opened from amDash Dialog, onClose is provided.
  // In that mode we pad the bottom by AppShell's bar height so content
  // isn't hidden under the translucent nav bar.
  const isDialogMode = Boolean(onClose);

  // Bottom clearance: AppShell bar + its gutter, plus device safe area
  const appShellPb = isDialogMode
    ? `calc(${APPSHELL_TOTAL}px + env(safe-area-inset-bottom, 0px))`
    : `env(safe-area-inset-bottom, 0px)`;

  /* ── State ── */
  const [activeTab,       setActiveTab]       = useState(0);
  const [selectedContact, setSelectedContact] = useState(null);
  const [rightPanel,      setRightPanel]      = useState(null);   // null | "detail" | "add"
  const [mobileShowRight, setMobileShowRight] = useState(false);
  const [searchTerm,      setSearchTerm]      = useState("");
  const [filterTag,       setFilterTag]       = useState("");
  const [sortBy,          setSortBy]          = useState("name");
  const [editing,         setEditing]         = useState({});
  const [newContact,      setNewContact]      = useState({
    profile_photo_link:"", first_name:"", last_name:"", email:"", phone:"",
    company:"", title:"", location:"", notes:"", ranking:"", tags:[],
  });
  const [openLogDlg, setOpenLogDlg] = useState(false);
  const [logForm,    setLogForm]    = useState({ id:null, contact_id:"", datetime:"", notes:"", rating:3 });
  const [snackMsg,   setSnackMsg]   = useState("");

  /* ── Data ── */
  const { data: contacts = [], isLoading: isContactsLoading, error: contactsErr } =
    useQuery(["contacts"], API.contacts.list, { refetchOnWindowFocus: false });
  const { data: summary } =
    useQuery(["commSummary"], API.contacts.logs.summary);
  const { data: logs = [], isLoading: logsLoad } =
    useQuery(["commLogs"], API.contacts.logs.listAll);

  const selfContact   = useMemo(() => contacts.find((c) => c.contact_type === "self"), [contacts]);
  const otherContacts = useMemo(() => contacts.filter((c) => c.contact_type !== "self"), [contacts]);

  const allTags = useMemo(() => {
    const s = new Set();
    otherContacts.forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return Array.from(s);
  }, [otherContacts]);

  const filteredContacts = useMemo(() => {
    let arr = otherContacts.filter((c) => {
      const fields = [c.first_name, c.last_name, c.email, c.phone, c.company, c.location, c.notes, String(c.ranking ?? ""), ...(c.tags || [])];
      return (
        (!searchTerm || fields.some((f) => f?.toString().toLowerCase().includes(searchTerm))) &&
        (!filterTag  || (c.tags || []).includes(filterTag))
      );
    });
    arr.sort((a, b) => {
      if (sortBy === "name")    return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      if (sortBy === "ranking") return (a.ranking || 999) - (b.ranking || 999);
      if (sortBy === "company") return (a.company || "").localeCompare(b.company || "");
      return 0;
    });
    return arr;
  }, [otherContacts, searchTerm, filterTag, sortBy]);

  useEffect(() => {
    const map = {};
    contacts.forEach((c) => { map[c.id] = { ...c }; });
    setEditing(map);
  }, [contacts]);

  /* ── Mutations ── */
  const updMut = useMutation(({ id, data }) => API.contacts.update(id, data), {
    onSuccess: () => { qc.invalidateQueries(["contacts"]); setSnackMsg("Contact saved!"); },
  });
  const delMut = useMutation((id) => API.contacts.remove(id), {
    onSuccess: () => {
      qc.invalidateQueries(["contacts"]);
      setSelectedContact(null); setRightPanel(null); setMobileShowRight(false);
      setSnackMsg("Contact deleted.");
    },
  });
  const addMut = useMutation((data) => API.contacts.create(data), {
    onSuccess: () => {
      qc.invalidateQueries(["contacts"]);
      setRightPanel(null); setMobileShowRight(false);
      setSnackMsg("Contact added!");
      setNewContact({ profile_photo_link:"", first_name:"", last_name:"", email:"", phone:"", company:"", title:"", location:"", notes:"", ranking:"", tags:[] });
    },
  });
  const createLogMut = useMutation((input) => API.contacts.logs.create(input), {
    onSuccess: () => { qc.invalidateQueries(["commLogs", "commSummary"]); setOpenLogDlg(false); setSnackMsg("Interaction logged!"); },
  });
  const updLogMut = useMutation(({ id, data }) => API.contacts.logs.update(id, data), {
    onSuccess: () => { qc.invalidateQueries(["commLogs", "commSummary"]); setOpenLogDlg(false); setSnackMsg("Log updated!"); },
  });
  const delLogMut = useMutation((id) => API.contacts.logs.remove(id), {
    onSuccess: () => { qc.invalidateQueries(["commLogs", "commSummary"]); setSnackMsg("Log deleted."); },
  });

  /* ── Handlers ── */
  const handleSelectContact = (c) => { setSelectedContact(c); setRightPanel("detail"); if (isMobile) setMobileShowRight(true); };
  const handleAddContact    = ()  => { setSelectedContact(null); setRightPanel("add"); if (isMobile) setMobileShowRight(true); };
  const handleEditField     = (field, value) => {
    if (!selectedContact) return;
    setEditing((p) => ({ ...p, [selectedContact.id]: { ...p[selectedContact.id], [field]: value } }));
  };
  const handleSaveContact = () => {
    if (!selectedContact) return;
    const d = { ...editing[selectedContact.id] };
    delete d.id;
    updMut.mutate({ id: selectedContact.id, data: d });
  };
  const handleQuickCall = (c) => {
    setLogForm({ id: null, contact_id: c.id, datetime: new Date().toISOString(), notes: "", rating: 3 });
    setOpenLogDlg(true);
  };
  const handleSaveLog = () => {
    const payload = { contact_id: logForm.contact_id, datetime: logForm.datetime, notes: logForm.notes, rating: logForm.rating };
    logForm.id ? updLogMut.mutate({ id: logForm.id, data: payload }) : createLogMut.mutate(payload);
  };
  const handleBackMobile = () => {
    setMobileShowRight(false);
    setTimeout(() => { setRightPanel(null); setSelectedContact(null); }, 300);
  };

  /* ── Loading / error guards ── */
  if (isContactsLoading || logsLoad) return (
    <Box sx={{ display:"flex", alignItems:"center", justifyContent:"center", height: embedded ? "100%" : "100vh" }}>
      <Stack alignItems="center" spacing={2}>
        <CircularProgress size={52} />
        <Typography variant="body2" color="text.secondary">Loading contacts…</Typography>
      </Stack>
    </Box>
  );
  if (contactsErr) return (
    <Box sx={{ p:4 }}><Alert severity="error" sx={{ borderRadius: 2 }}>Failed to load contacts. Please try refreshing.</Alert></Box>
  );

  /* ── Left panel (shared) ── */
  const LeftPanelContent = () => (
    <Box sx={{ display:"flex", flexDirection:"column", height:"100%" }}>
      <Box sx={{ p:2, borderBottom:`1px solid ${alpha(theme.palette.divider, 0.08)}`, flexShrink:0 }}>
        <TextField
          fullWidth size="small" placeholder="Search contacts…" value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize:18 }} /></InputAdornment> }}
          sx={{ mb:1.5, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
        />
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ flex:1 }}>
            <InputLabel sx={{ fontSize:"0.8rem" }}>Tag</InputLabel>
            <Select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} label="Tag" sx={{ fontSize:"0.8rem", borderRadius: 2 }}>
              <MenuItem value="">All tags</MenuItem>
              {allTags.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex:1 }}>
            <InputLabel sx={{ fontSize:"0.8rem" }}>Sort</InputLabel>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort" sx={{ fontSize:"0.8rem", borderRadius: 2 }}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="ranking">Ranking</MenuItem>
              <MenuItem value="company">Company</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || filterTag) && (
            <IconButton size="small" onClick={() => { setSearchTerm(""); setFilterTag(""); }} sx={{ flexShrink:0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>
      <Box sx={{ px:2, py:1, flexShrink:0 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {filteredContacts.length} contact{filteredContacts.length !== 1 ? "s" : ""}
          {searchTerm && ` · "${searchTerm}"`}
          {filterTag && ` · ${filterTag}`}
        </Typography>
      </Box>
      <Box sx={{ flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
        {filteredContacts.length === 0 ? (
          <Box sx={{ p:4, textAlign:"center", color:"text.secondary" }}>
            <GroupsIcon sx={{ fontSize:48, opacity:0.18, mb:1 }} />
            <Typography variant="body2" sx={{ opacity:0.4 }}>No contacts found</Typography>
          </Box>
        ) : filteredContacts.map((c) => (
          <ContactRow key={c.id} contact={c} selected={selectedContact} onClick={handleSelectContact} />
        ))}
      </Box>
    </Box>
  );

  /* ── Right panel router (shared) ── */
  const RightPanelContent = () => {
    if (rightPanel === "add") return (
      <AddContactPanel
        data={newContact}
        onChange={(field, val) => setNewContact((p) => ({ ...p, [field]: val }))}
        onSave={() => addMut.mutate({
          ...newContact,
          ranking: newContact.ranking || null,
          full_name: `${newContact.first_name} ${newContact.last_name}`.trim(),
        })}
        onClose={() => { setRightPanel(null); setMobileShowRight(false); }}
        saving={addMut.isLoading}
        isMobile={isMobile}
      />
    );
    return (
      <ContactDetailPanel
        contact={selectedContact}
        editing={editing[selectedContact?.id]}
        onChange={handleEditField}
        onSave={handleSaveContact}
        onDelete={(id) => delMut.mutate(id)}
        onQuickCall={handleQuickCall}
        saving={updMut.isLoading}
        isMobile={isMobile}
      />
    );
  };

  /* ── Snackbar bottom offset ── */
  const snackSx = { mb: appShellPb };

  /* ─────────────────── MOBILE LAYOUT ─────────────────── */
  if (isMobile) {
    const rootSx = embedded
      ? { height:"100%", display:"flex", flexDirection:"column", bgcolor:"background.default", overflow:"hidden" }
      : { position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", flexDirection:"column", bgcolor:"background.default", overflow:"hidden", paddingTop:"env(safe-area-inset-top, 0px)" };

    return (
      <Box sx={rootSx}>
        <AlertDialog alertState={dialogState} />

        {/* ── Mobile header ── */}
        <Box sx={{
          height: 60, flexShrink: 0,
          bgcolor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width:34, height:34, borderRadius:1.5, bgcolor:alpha(theme.palette.primary.main, 0.1), display:"flex", alignItems:"center", justifyContent:"center" }}>
              <GroupsIcon sx={{ fontSize:20, color:"primary.main" }} />
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ fontSize:"1.05rem" }}>Contact Hub</Typography>
            <Chip label={otherContacts.length} size="small" sx={{ fontWeight:700, height:20, fontSize:"0.7rem", borderRadius:1 }} />
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              variant="contained" size="small"
              startIcon={<AddIcon sx={{ fontSize:"1rem !important" }} />}
              onClick={handleAddContact}
              sx={{ textTransform:"none", borderRadius:2, fontSize:"0.8rem", py:0.5, px:1.5, minWidth:0 }}
            >
              Add
            </Button>

            {/* X close — only in dialog mode */}
            {onClose && (
              <IconButton
                size="small" onClick={onClose}
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  color: "error.main",
                  border: `1.5px solid ${alpha(theme.palette.error.main, 0.25)}`,
                  borderRadius: 1.5,
                  transition: "all 0.2s",
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.15) },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* ── Tabs ── */}
        <Box sx={{ flexShrink:0, borderBottom:`1px solid ${theme.palette.divider}`, bgcolor:"background.paper" }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth"
            sx={{ minHeight:44, "& .MuiTab-root": { textTransform:"none", fontSize:"0.78rem", fontWeight:600, minHeight:44, py:0 } }}>
            <Tab label="Contacts" icon={<GroupsIcon sx={{ fontSize:16 }} />} iconPosition="start" />
            <Tab label="Logs" icon={<TimelineIcon sx={{ fontSize:16 }} />} iconPosition="start" />
            <Tab label="Stats" icon={<AssessmentIcon sx={{ fontSize:16 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* ── Body with slide panels ── */}
        <Box sx={{ flex:1, overflow:"hidden", position:"relative" }}>
          {/* Left layer */}
          <Box sx={{
            position:"absolute", inset:0,
            transform: mobileShowRight ? "translateX(-100%)" : "translateX(0)",
            transition:"transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            display:"flex", flexDirection:"column",
            pb: appShellPb,
          }}>
            {activeTab === 0 && <LeftPanelContent />}
            {activeTab === 1 && (
              <CallLogsPanel logs={logs} contacts={contacts}
                onAdd={() => { setLogForm({ id:null, contact_id:"", datetime:new Date().toISOString(), notes:"", rating:3 }); setOpenLogDlg(true); }}
                onEdit={(log) => { setLogForm({ id:log.id, contact_id:log.contact_id, datetime:log.datetime_of_call, notes:log.notes||"", rating:log.rating||3 }); setOpenLogDlg(true); }}
                onDelete={(id) => delLogMut.mutate(id)} />
            )}
            {activeTab === 2 && <AnalyticsPanel contacts={otherContacts} logs={logs} summary={summary} />}
          </Box>

          {/* Right layer (detail/add) */}
          <Box sx={{
            position:"absolute", inset:0, bgcolor:"background.default",
            transform: mobileShowRight ? "translateX(0)" : "translateX(100%)",
            transition:"transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            display:"flex", flexDirection:"column",
          }}>
            {/* Back bar */}
            <Box sx={{ height:52, bgcolor:"background.paper", borderBottom:`1px solid ${theme.palette.divider}`, display:"flex", alignItems:"center", px:1.5, flexShrink:0 }}>
              <IconButton onClick={handleBackMobile} size="small" sx={{ mr:0.5 }}><ArrowBackIcon /></IconButton>
              <Typography variant="subtitle1" fontWeight={700}>
                {rightPanel === "add" ? "New Contact" : selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : ""}
              </Typography>
            </Box>
            <Box sx={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", pb: appShellPb }}>
              <RightPanelContent />
            </Box>
          </Box>
        </Box>

        {/* Self-profile FAB — standalone mode only, clear of AppShell */}
        {selfContact && !embedded && !onClose && (
          <Tooltip title="My Profile" placement="top">
            <IconButton
              onClick={() => handleSelectContact(selfContact)}
              sx={{
                position:"fixed",
                bottom:`calc(${theme.spacing(3)} + env(safe-area-inset-bottom, 0px))`,
                right:theme.spacing(3),
                width:54, height:54, bgcolor:"primary.main", color:"white",
                boxShadow:`0 8px 24px ${alpha(theme.palette.primary.main, 0.45)}`,
                transition:"all 0.3s",
                "&:hover": { bgcolor:"primary.dark", transform:"scale(1.1)" },
              }}
            >
              <PersonIcon sx={{ fontSize:26 }} />
            </IconButton>
          </Tooltip>
        )}

        <CallLogDialog open={openLogDlg} onClose={() => setOpenLogDlg(false)} logForm={logForm} setLogForm={setLogForm} contacts={contacts} onSave={handleSaveLog} onDelete={(id) => delLogMut.mutate(id)} saving={createLogMut.isLoading || updLogMut.isLoading} />
        <Snackbar open={!!snackMsg} autoHideDuration={3000} onClose={() => setSnackMsg("")} anchorOrigin={{ vertical:"bottom", horizontal:"center" }} sx={snackSx}>
          <Alert severity="success" onClose={() => setSnackMsg("")} icon={<CheckCircleIcon fontSize="inherit" />} sx={{ borderRadius:2 }}>{snackMsg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  /* ─────────────────── DESKTOP LAYOUT ─────────────────── */
  const rootSx = embedded
    ? { height:"100%", display:"flex", flexDirection:"column", bgcolor:"background.default", overflow:"hidden" }
    : { position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", flexDirection:"column", bgcolor:"background.default", overflow:"hidden" };

  return (
    <Box sx={rootSx}>
      <AlertDialog alertState={dialogState} />

      {/* ── 72px header ── */}
      <Box sx={{
        height:72, flexShrink:0,
        bgcolor:"background.paper",
        borderBottom:`1px solid ${theme.palette.divider}`,
        boxShadow:`0 1px 4px ${alpha(theme.palette.common.black, 0.06)}`,
        display:"flex", alignItems:"center", justifyContent:"space-between", px:4,
      }}>
        {/* Left: logo + count */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ width:44, height:44, borderRadius:2, bgcolor:alpha(theme.palette.primary.main, 0.1), display:"flex", alignItems:"center", justifyContent:"center" }}>
            <GroupsIcon sx={{ fontSize:26, color:"primary.main" }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight:1.2 }}>Contact Hub</Typography>
            <Typography variant="caption" color="text.secondary">Manage your professional network</Typography>
          </Box>
          <Chip label={`${otherContacts.length} contacts`} size="small" sx={{ ml:1, fontWeight:600, borderRadius:1.5 }} />
        </Stack>

        {/* Center: tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); if(v !== 0) { setRightPanel(null); setSelectedContact(null); } }}
          sx={{ "& .MuiTab-root": { textTransform:"none", fontSize:"0.9rem", fontWeight:600, minHeight:72 } }}
        >
          <Tab icon={<GroupsIcon sx={{ fontSize:20 }} />} iconPosition="start" label="Contacts" />
          <Tab icon={<TimelineIcon sx={{ fontSize:20 }} />} iconPosition="start" label={`Logs (${logs.length})`} />
          <Tab icon={<AssessmentIcon sx={{ fontSize:20 }} />} iconPosition="start" label="Analytics" />
        </Tabs>

        {/* Right: Add + optional X */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained" size="large"
            startIcon={<AddIcon />} onClick={handleAddContact}
            sx={{ textTransform:"none", borderRadius:2, px:3, fontWeight:700, boxShadow:3 }}
          >
            Add Contact
          </Button>

          {/* X close — dialog mode only */}
          {onClose && (
            <Tooltip title="Close Contact Hub">
              <IconButton
                onClick={onClose}
                sx={{
                  width:44, height:44,
                  bgcolor: alpha(theme.palette.error.main, 0.07),
                  color: "error.main",
                  border: `1.5px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  transition:"all 0.22s",
                  "&:hover": {
                    bgcolor: "error.main", color:"white",
                    transform:"rotate(90deg)",
                    boxShadow:`0 4px 14px ${alpha(theme.palette.error.main, 0.4)}`,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex:1, display:"flex", overflow:"hidden", pb: isDialogMode ? `${APPSHELL_TOTAL}px` : 0 }}>
        {activeTab === 0 ? (
          <>
            {/* Contact list — 340px */}
            <Box sx={{ width:340, flexShrink:0, borderRight:`1px solid ${theme.palette.divider}`, bgcolor:"background.paper", display:"flex", flexDirection:"column", overflow:"hidden" }}>
              <LeftPanelContent />
            </Box>
            {/* Detail / add / empty */}
            <Box sx={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", bgcolor:alpha(theme.palette.primary.main, 0.01) }}>
              <Fade in key={`${rightPanel}-${selectedContact?.id}`} timeout={200}>
                <Box sx={{ height:"100%" }}>
                  <RightPanelContent />
                </Box>
              </Fade>
            </Box>
          </>
        ) : activeTab === 1 ? (
          <Box sx={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
            <CallLogsPanel
              logs={logs} contacts={contacts}
              onAdd={() => { setLogForm({ id:null, contact_id:"", datetime:new Date().toISOString(), notes:"", rating:3 }); setOpenLogDlg(true); }}
              onEdit={(log) => { setLogForm({ id:log.id, contact_id:log.contact_id, datetime:log.datetime_of_call, notes:log.notes||"", rating:log.rating||3 }); setOpenLogDlg(true); }}
              onDelete={(id) => delLogMut.mutate(id)} />
          </Box>
        ) : (
          <Box sx={{ flex:1, overflow:"hidden" }}>
            <AnalyticsPanel contacts={otherContacts} logs={logs} summary={summary} />
          </Box>
        )}
      </Box>

      {/* Self-profile FAB — standalone only */}
      {selfContact && !embedded && !onClose && (
        <Tooltip title="My Profile" placement="left">
          <IconButton
            onClick={() => { setActiveTab(0); handleSelectContact(selfContact); }}
            sx={{
              position:"fixed", bottom:theme.spacing(3), right:theme.spacing(3),
              width:58, height:58, bgcolor:"primary.main", color:"white",
              boxShadow:`0 8px 28px ${alpha(theme.palette.primary.main, 0.45)}`,
              transition:"all 0.3s",
              "&:hover": { bgcolor:"primary.dark", transform:"scale(1.08)", boxShadow:`0 12px 36px ${alpha(theme.palette.primary.main, 0.55)}` },
            }}
          >
            <PersonIcon sx={{ fontSize:28 }} />
          </IconButton>
        </Tooltip>
      )}

      <CallLogDialog open={openLogDlg} onClose={() => setOpenLogDlg(false)} logForm={logForm} setLogForm={setLogForm} contacts={contacts} onSave={handleSaveLog} onDelete={(id) => delLogMut.mutate(id)} saving={createLogMut.isLoading || updLogMut.isLoading} />
      <Snackbar open={!!snackMsg} autoHideDuration={3000} onClose={() => setSnackMsg("")} anchorOrigin={{ vertical:"bottom", horizontal:"center" }} sx={snackSx}>
        <Alert severity="success" onClose={() => setSnackMsg("")} icon={<CheckCircleIcon fontSize="inherit" />} sx={{ borderRadius:2 }}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}